import CanvasObject from '../CanvasObject';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import type BBoxObject from './BBoxObject';
import { BBOX_COLOR } from './common';

export const RADIUS = 5;
export const HANDLE_LENGTH = 30;

export interface RotateHandleOptions {
  midpoint: Point;
  bbox: BBoxObject;
}

export default class RotateHandle extends CanvasObject {
  lngLat: LngLat;
  bbox: BBoxObject;
  point: Point;
  radius: number = RADIUS;

  constructor(id: string, surface: SurfaceBase, options: RotateHandleOptions) {
    super(id, surface);

    this.interactive = true;
    this.lngLat = this.surface.toMapCoords(options.midpoint);
    this.bbox = options.bbox;

    this.point = this.computeHandlePointWithOffset();

    this.on('hoverStart', this.onHoverStart);
    this.on('hoverEnd', this.onHoverEnd);

    this.on('dragStart', () => {
      this.surface.setCursor('grabbing');
      this.bbox.emit('rotateStart');
    });

    this.on('dragging', (e) => {
      const c = this.bbox.getCenter();
      const dAx = e.startPoint.x - c.x;
      const dAy = e.startPoint.y - c.y;
      const dBx = e.point.x - c.x;
      const dBy = e.point.y - c.y;

      const angle = Math.atan2(dAx * dBy - dAy * dBx, dAx * dBx + dAy * dBy);

      this.bbox.emit('rotating', { angle });
      this.point = this.computeHandlePointWithOffset();
    });

    this.on('dragEnd', () => {
      this.bbox.emit('rotateEnd');
      this.point = this.computeHandlePointWithOffset();
      this.refreshMapCoords();
      this.surface.resetCursor();
    });

    this.on('scaleStart', () => {});
    this.on('scaling', (e) => {
      this.point = this.computeHandlePointWithOffset();
    });
    this.on('scaleEnd', () => {
      this.lngLat = this.surface.toMapCoords(this.getMidpointOnBBoxLine());
      this.refreshScreenCoords();
    });
  }

  onHoverStart = () => {
    const svg =
      '<svg fill="#000000" width="18px" height="18px" viewBox="0 0 0.54 0.54" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M0.113 0.247a0.022 0.022 0 0 1 0.022 0.019v0.008A0.135 0.135 0 0 0 0.282 0.405L0.276 0.399A0.022 0.022 0 0 1 0.307 0.368l0.045 0.045a0.022 0.022 0 0 1 0 0.032l-0.045 0.045A0.022 0.022 0 0 1 0.276 0.458L0.283 0.45A0.181 0.181 0 0 1 0.27 0.45 0.18 0.18 0 0 1 0.09 0.282V0.27A0.022 0.022 0 0 1 0.113 0.247ZM0.264 0.051a0.022 0.022 0 0 1 0.001 0.03L0.264 0.082 0.257 0.09A0.18 0.18 0 0 1 0.45 0.27a0.022 0.022 0 0 1 -0.045 0A0.135 0.135 0 0 0 0.258 0.135l0.006 0.006a0.022 0.022 0 0 1 -0.03 0.033V0.172l-0.045 -0.045A0.022 0.022 0 0 1 0.187 0.097L0.188 0.096l0.045 -0.045a0.022 0.022 0 0 1 0.032 0Z"/></svg>';

    var encodedData = window.btoa(svg);
    const cursor = `url('data:image/svg+xml;base64,${encodedData}') 12 12, auto`;
    this.surface.setCursor(cursor);
    this.radius = 2 * RADIUS;
    this.renderAll();
  };

  onHoverEnd = () => {
    this.surface.resetCursor();
    this.radius = RADIUS;
    this.renderAll();
  };

  getMidpointOnBBoxLine = (): Point => {
    const p1 = this.bbox.resizeHandles[0].point;
    const p2 = this.bbox.resizeHandles[1].point;
    const p: Point = {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
    return p;
  };

  computeHandlePointWithOffset = () => {
    const p0 = this.getMidpointOnBBoxLine();

    const center = this.bbox.getCenter();

    const d1x = p0.x - center.x;
    const d1y = p0.y - center.y;
    const l = Math.hypot(d1x, d1y);
    const angle = Math.atan2(d1y, d1x);

    const newL = l + HANDLE_LENGTH;
    const dx = newL * Math.cos(angle);
    const dy = newL * Math.sin(angle);
    const p: Point = {
      x: center.x + dx,
      y: center.y + dy,
    };

    return p;
  };

  refreshScreenCoords(): void {
    this.point = this.computeHandlePointWithOffset();
  }

  refreshMapCoords(): void {
    const p = this.getMidpointOnBBoxLine();
    this.lngLat = this.surface.toMapCoords(p);
  }

  render(
    ctx: CanvasRenderingContext2D,
    strokeColor?: string | undefined,
    strokeWidth?: number | undefined,
    isDashed?: boolean | undefined,
    strokeOpacity?: number | undefined,
    isBorderHidden?: boolean | undefined,
  ): void {
    const r = this.radius;
    const p = this.point;

    const handlePoint = this.getMidpointOnBBoxLine();

    ctx.save();
    ctx.fillStyle = BBOX_COLOR;
    ctx.strokeStyle = BBOX_COLOR;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(handlePoint.x, handlePoint.y);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, 2 * Math.PI);
    ctx.fill();
    ctx.restore();
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D, color: string): void {
    const r = this.radius * 2;
    const p = this.point;

    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, 2 * Math.PI);
    ctx.fill();
    ctx.restore();
  }
}
